'use client';

import { ISalePageDetailResponseProductOptionCategory } from '@/api/types';
import type { OptionCategoryForDropdown } from '@/components/dropdown';
import { Dropdown, OptionCategoryDropdownList } from '@/components/dropdown';
import { qaAttr } from '@/lib/qa';
import { Column, Divider, Element, Icon, Row } from '@vuddy/components';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PurchaseItemContent } from '../common/PurchaseItemContent';
import type { ProductItemWithAdditionalInfo } from '../common/useProductItemList';
import * as styles from './dropdown.css';
import { SelectedProductOption } from './types';

export function isSameCartItem(
  a: SelectedProductOption,
  b: SelectedProductOption
): boolean {
  if (a.productId !== b.productId) return false;
  if (!a.optionCategories && !b.optionCategories) return true;
  if (!a.optionCategories || !b.optionCategories) return false;
  if (a.optionCategories.length !== b.optionCategories.length) return false;
  return a.optionCategories.every((catA, idx) => {
    const catB = b.optionCategories![idx];
    if (catA.optionCategoryId !== catB.optionCategoryId) return false;
    const idsA = [...catA.optionMenuIds].sort().join(',');
    const idsB = [...catB.optionMenuIds].sort().join(',');
    return idsA === idsB;
  });
}

interface ProductSelectDropdownProps {
  options: ProductItemWithAdditionalInfo[];
  cartItems: SelectedProductOption[];
  setCartItems: React.Dispatch<React.SetStateAction<SelectedProductOption[]>>;
  // 부모 요소의 넓이를 사용하여 드롭다운의 넓이를 설정
  hasParentWidth?: boolean;
  qaId?: string;
}

type ProductDropdownOption = ProductItemWithAdditionalInfo & {
  label: string;
  disabled: boolean;
};

type DividerOption = { id: string; isDivider: true; label: string };

export const ProductSelectDropdown = ({
  options,
  cartItems,
  setCartItems,
  hasParentWidth = false,
  qaId,
}: ProductSelectDropdownProps) => {
  // 드롭다운 오픈 상태를 통합 관리
  const [openedDropdown, setOpenedDropdown] = useState<
    'product' | number | null
  >(null);
  // 현재 선택된 상품 정보
  const [selectedProductName, setSelectedProductName] = useState<string | null>(
    null
  );
  const [selectedOptionCategories, setSelectedOptionCategories] = useState<
    ISalePageDetailResponseProductOptionCategory[] | null
  >(null);
  const [selectedOptionMenus, setSelectedOptionMenus] = useState<{
    [categoryId: number]: number[];
  }>({});

  const selectedOptionMenuNames = useMemo(() => {
    if (!selectedOptionCategories)
      return {} as { [categoryId: number]: string };
    return Object.fromEntries(
      selectedOptionCategories.map(cat => {
        const selectedIds = selectedOptionMenus[cat.id] || [];
        if (selectedIds.length === 0) return [cat.id, ''];
        if (selectedIds.includes(-1)) return [cat.id, '선택안함'];
        return [
          cat.id,
          cat.options
            .filter(o => selectedIds.includes(o.id))
            .map(o => o.name)
            .join(', '),
        ];
      })
    ) as { [categoryId: number]: string };
  }, [selectedOptionCategories, selectedOptionMenus]);

  const dropdownOptions = useMemo(
    () =>
      options.map(item => ({
        ...item,
        label: item.name,
        disabled: item.saleStatus !== 'ON_SALE' || item.remainQuantity === 0,
      })),
    [options]
  );

  const optionsWithDivider = useMemo(
    () =>
      dropdownOptions.flatMap(
        (option, idx, arr): (ProductDropdownOption | DividerOption)[] =>
          idx < arr.length - 1
            ? [
                option,
                { id: `divider-${idx}`, isDivider: true as const, label: '' },
              ]
            : [option]
      ),
    [dropdownOptions]
  );

  const renderOption = (item: ProductDropdownOption | DividerOption) => {
    if ('isDivider' in item) return <Divider />;
    return (
      <Element
        className={styles.optionItem}
        aria-disabled={item.disabled ? 'true' : undefined}
        data-disabled={item.disabled ? 'true' : undefined}>
        <PurchaseItemContent
          item={{ ...item, isPurchasable: !item.disabled }}
          quantity={1}
        />
      </Element>
    );
  };

  const handleProductMenuClick = (id: string | number) => {
    if (typeof id === 'number') {
      const optionMeta = dropdownOptions.find(opt => opt.id === id);
      if (optionMeta?.disabled) return;
      const selectedItem = options.find(item => item.id === id);
      if (!selectedItem) return;
      if (selectedItem.saleStatus !== 'ON_SALE') {
        return;
      }
      if (selectedItem.remainQuantity === 0) {
        return;
      }
      if (
        selectedItem &&
        selectedItem.optionCategories &&
        selectedItem.optionCategories.length > 0
      ) {
        //옵션 있는 상품 선택 시
        setSelectedOptionCategories(selectedItem.optionCategories);
        setSelectedProductName(selectedItem.name);
      } else if (selectedItem) {
        // 옵션이 없는 상품 선택 시
        setSelectedOptionCategories(null);
        setSelectedProductName(null);

        // 중복 체크
        const newItem = {
          productId: selectedItem.id,
          quantity: 1,
          totalPrice: selectedItem.price.amount,
        };
        const isDuplicate = cartItems.some(item =>
          isSameCartItem(item, newItem)
        );

        if (isDuplicate) {
          toast(`이미 등록된 상품이 있습니다.`);
          return;
        }

        // 카트에 상품 추가
        setCartItems([
          ...cartItems.filter(
            item =>
              !(item.productId === selectedItem.id && !item.optionCategories)
          ),
          newItem,
        ]);
      }
    }
    setOpenedDropdown(null);
  };

  const handleOptionMenuClick = ({
    categoryId,
    optionMenuId,
  }: {
    categoryId: number;
    optionMenuId: number;
  }) => {
    const category = selectedOptionCategories?.find(
      cat => cat.id === categoryId
    );
    if (!category) return;

    // 품절된 옵션 클릭 시 무시
    if (optionMenuId !== -1) {
      const option = category.options.find(opt => opt.id === optionMenuId);
      if (option?.saleStatus === 'SOLD_OUT') {
        return;
      }
    }

    setSelectedOptionMenus(prev => {
      const prevArr = prev[categoryId] || [];

      // 선택안함(-1) 선택 시: 다른 모든 선택 해제, -1만 남김
      if (optionMenuId === -1) {
        return {
          ...prev,
          [categoryId]: [-1],
        };
      }

      // 다른 옵션 선택 시: 선택안함(-1) 해제
      let newArr = prevArr.filter(id => id !== -1);

      if (category.isMultipleSelectable) {
        if (newArr.includes(optionMenuId)) {
          newArr = newArr.filter(id => id !== optionMenuId);
        } else {
          newArr = [...newArr, optionMenuId];
        }
      } else {
        newArr = [optionMenuId];
      }

      return {
        ...prev,
        [categoryId]: newArr,
      };
    });
  };

  const handleSelectDone = (
    category: ISalePageDetailResponseProductOptionCategory
  ) => {
    if (!selectedProductName) return;
    const product = options.find(item => item.name === selectedProductName);
    if (!product || product.saleStatus !== 'ON_SALE') {
      return;
    }
    if (product.remainQuantity === 0) {
      return;
    }

    // 모든 카테고리의 선택 상태 확인
    const allSelected =
      selectedOptionCategories?.every(
        cat => (selectedOptionMenus[cat.id] || []).length > 0
      ) ?? false;

    if (!allSelected) {
      return;
    }

    // 선택된 옵션들의 가격 합산
    const totalOptionPrice =
      selectedOptionCategories?.reduce((total, cat) => {
        const selectedOptionIds = selectedOptionMenus[cat.id] || [];
        const categoryOptions = cat.options.filter(opt =>
          selectedOptionIds.includes(opt.id)
        );
        const categoryTotal = categoryOptions.reduce(
          (sum, opt) => sum + opt.price.amount,
          0
        );
        return total + categoryTotal;
      }, 0) ?? 0;

    const newCartItem: SelectedProductOption = {
      productId: product.id,
      optionCategories: selectedOptionCategories?.map(cat => ({
        optionCategoryId: cat.id,
        optionMenuIds: selectedOptionMenus[cat.id] || [],
      })),
      quantity: 1,
      totalPrice: product.price.amount + totalOptionPrice, // 기본 가격 + 옵션 가격 합산
    };

    // 중복 체크
    const isDuplicate = cartItems.some(item =>
      isSameCartItem(item, newCartItem)
    );
    if (isDuplicate) {
      toast(`이미 등록된 상품이 있습니다.`);
      return;
    }

    setCartItems([
      ...cartItems.filter(item => !isSameCartItem(item, newCartItem)),
      newCartItem,
    ]);

    // 상태 초기화
    setSelectedProductName(null);
    setSelectedOptionCategories(null);
    setSelectedOptionMenus({});
  };

  const handleClickDropdown = () => {
    setOpenedDropdown(openedDropdown === 'product' ? null : 'product');
  };

  return (
    <Column gap="gap_base" {...(qaId ? qaAttr(qaId) : {})}>
      <Dropdown
        hasParentWidth={hasParentWidth}
        open={openedDropdown === 'product'}
        options={optionsWithDivider}
        triggerClassName={styles.trigger}
        contentClassName={styles.content}
        onMenuClick={handleProductMenuClick}
        offset={8}
        fallbackPlacements={['top-start', 'top-end']}
        enableSizeMiddleware={true}
        renderOption={renderOption}>
        <Row width="100%" onClick={handleClickDropdown}>
          <Element
            flex={1}
            color={selectedProductName ? 'primary' : 'placeholder'}
            typography="bodyBase">
            {selectedProductName
              ? `(상품) ${selectedProductName}`
              : '상품을 선택해주세요'}
          </Element>
          <Icon
            name={
              openedDropdown === 'product'
                ? 'FILL_TRIANGLE_UP'
                : 'FILL_TRIANGLE_DOWN'
            }
            size="16px"
            color={selectedProductName ? 'primary' : 'placeholder'}
          />
        </Row>
      </Dropdown>
      {/* 옵션이 있는 상품 > 옵션 카테고리 드롭다운들 */}
      {selectedOptionCategories && (
        <OptionCategoryDropdownList
          hasParentWidth={hasParentWidth}
          categories={selectedOptionCategories.map(
            (category): OptionCategoryForDropdown => {
              const options = category.isSelectionRequired
                ? category.options
                : [
                    {
                      id: -1,
                      name: '선택안함',
                      price: { amount: 0 },
                    },
                    ...category.options,
                  ];
              return {
                id: category.id,
                name: category.name,
                options: options.map(opt => ({
                  id: opt.id,
                  label: opt.name,
                  disabled:
                    opt.id !== -1 &&
                    'saleStatus' in opt &&
                    opt.saleStatus === 'SOLD_OUT',
                  isSoldOut:
                    opt.id !== -1 &&
                    'saleStatus' in opt &&
                    opt.saleStatus === 'SOLD_OUT',
                  priceLabel:
                    'price' in opt && opt.price.amount !== 0
                      ? `${opt.price.amount > 0 ? '+' : ''}${opt.price.amount.toLocaleString()}원`
                      : opt.id !== -1
                        ? '0원'
                        : undefined,
                })),
              };
            }
          )}
          selectedIds={selectedOptionMenus}
          openedCategoryId={
            openedDropdown !== 'product' ? openedDropdown : null
          }
          onOpenedCategoryChange={id => setOpenedDropdown(id)}
          onSelect={(categoryId, optionId) =>
            handleOptionMenuClick({
              categoryId,
              optionMenuId: Number(optionId),
            })
          }
          onComplete={categoryId => {
            const category = selectedOptionCategories.find(
              c => c.id === categoryId
            );
            if (category) handleSelectDone(category);
          }}
        />
      )}
    </Column>
  );
};
